import { cn } from '@/utils/cn'
import type { ColorPreset } from '@/components/Configuracao/appearanceColorPresets'

type Props = {
  currentMode: 'dark' | 'light'
  activePreset: string | null
  allPresets: ColorPreset[]
  onApplyPreset: (preset: ColorPreset) => void
}

export function AppearanceCoresPresetsSection({
  currentMode,
  activePreset,
  allPresets,
  onApplyPreset,
}: Props) {
  return (
    <div className="glass-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-text-primary font-semibold text-sm">
            Paletas Prontas — {currentMode === 'dark' ? '🌙 Dark' : '☀️ Light'}
          </h3>
          <p className="text-text-muted text-[11px] mt-0.5">
            Clique para aplicar um tema pronto. Você pode ajustar manualmente depois.
          </p>
        </div>
        {activePreset && (
          <span className="text-[10px] font-bold text-accent bg-accent/10 border border-accent/20 px-2 py-1 rounded-full">
            {allPresets.find((p) => p.id === activePreset)?.name}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {allPresets.map((preset) => {
          const isActive = activePreset === preset.id
          const isBaseTheme = preset.id === 'dark-default' || preset.id === 'light-white'
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onApplyPreset(preset)}
              className={cn(
                'group relative flex flex-col gap-3 p-3.5 rounded-xl border text-left transition-all hover:scale-[1.02]',
                isBaseTheme && !isActive && 'border-white/[0.12] bg-white/[0.05]',
                isActive
                  ? 'border-accent bg-accent/10 shadow-lg shadow-accent/20'
                  : !isBaseTheme &&
                      'border-white/[0.06] bg-white/[0.03] hover:border-white/[0.15] hover:bg-white/[0.06]',
              )}
            >
              {isBaseTheme && !isActive && (
                <span className="absolute top-2 left-2 text-[9px] font-bold text-text-muted">
                  {currentMode === 'dark' ? '🌙 Padrão' : '☀️ Padrão'}
                </span>
              )}
              <div className={cn('flex items-center gap-1.5', isBaseTheme && 'mt-3')}>
                {preset.swatch.map((color, i) => (
                  <div
                    key={i}
                    className="rounded-full border border-white/10 shrink-0"
                    style={{
                      backgroundColor: color,
                      width: i === 0 ? '22px' : '14px',
                      height: i === 0 ? '22px' : '14px',
                    }}
                  />
                ))}
              </div>
              <div>
                <p
                  className={cn(
                    'text-xs font-semibold leading-tight',
                    isActive ? 'text-accent' : 'text-text-primary',
                  )}
                >
                  {preset.name}
                </p>
                <p className="text-[10px] text-text-muted mt-0.5 leading-tight">{preset.description}</p>
              </div>
              {isActive && (
                <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-accent flex items-center justify-center">
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path
                      d="M1.5 4L3.2 5.7L6.5 2.5"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
